/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { ComponentProps, forwardRef } from 'react';
import '../common/links.sass';
import './button-link.sass';

export enum ButtonShape {
  PILL = 'pill-button',
  STACKED = 'stacked-button',
  TEXT = 'text-link',
}

type LabelProps =
  | { 'aria-label'?: string; children: React.ReactNode }
  | { 'aria-label': string; children?: React.ReactNode };

export interface BaseProps {
  // pill or stacked
  shape?: ButtonShape;
  // want an outline around it?
  outline?: boolean;
  // should it shrink down to just an icon on small screens?
  mobileResponsive?: boolean;
  // extra classes
  className?: string;
  // primary buttons are green
  primary?: boolean;
  // is this button part of a group of similar buttons?
  isInGroup?: boolean;
  // is this button initially selected / active / highlighted?
  active?: boolean;

  disabled?: boolean;
  title?: string;

  // contents
  icon?: React.ReactNode;

  // qa
  dataTestId?: string;
}

// GW: used on both Link and Button
type CommonProps = BaseProps & LabelProps;

// GW: an exportable button type for other Components to extend, without the 'aria-label' requirement.
export type BaseButtonProps = BaseProps & React.ComponentProps<'button'>;

export type ButtonProps = BaseButtonProps & LabelProps;

export type LinkProps = CommonProps & {
  external?: boolean;
  // KU – make href ambiguous
  href?: unknown;
  inline?: boolean;
};

interface CommonAttributes {
  className: string;
  'data-testid': string | undefined;
}

interface ContentType {
  contents: React.ReactNode;
  attributes: CommonAttributes & Partial<CommonProps>;
}

function createContent<P extends CommonProps>(props: P): ContentType {
  const {
    shape = ButtonShape.PILL,
    mobileResponsive = false,
    outline = false,
    primary = false,
    active = false,
    isInGroup = false,
    disabled = false,
    className: extraClasses = '',

    icon = null,

    // a11y
    children = null,
    'aria-label': ariaLabel = '',
    title = '',

    // qa
    dataTestId = '',

    // rest
    ...rest
  } = props;

  const hasLabelComponent = children !== null && children !== undefined && children !== false;

  const classes = classNames({
    'general-button-link': true,
    'default-button-color': !primary, // start this off with default color if not primary
    'primary-button-color': primary,
    grouped: isInGroup,
    selected: active,
    outline,
    disabled,
    [shape]: true,
    'label-only': !icon && hasLabelComponent,
    'label-with-icon': icon && hasLabelComponent,
    'icon-only': icon && !hasLabelComponent,
    responsive: mobileResponsive,
    [extraClasses]: true,
  });

  const attributes = {
    className: classes,
    title: title || ariaLabel || undefined,
    'aria-label': ariaLabel || undefined,
    'data-testid': dataTestId || undefined,
    ...rest,
  };

  const iconComp = (
    <span className="buttonlink-icon" key="icon">
      {icon}
    </span>
  );
  const labelComp = hasLabelComponent && (
    <span className="buttonlink-label" key="label">
      {children}
    </span>
  );

  let contents = iconComp;

  if (icon !== null && hasLabelComponent) {
    contents = (
      <>
        {iconComp}
        {labelComp}
      </>
    );
  } else if (labelComp) {
    contents = labelComp;
  }

  return { attributes, contents };
}

function hasContents(props: CommonProps): boolean {
  const { children, icon } = props;
  const hasLabel = children !== null;
  return !!(hasLabel || icon);
}

export const Button = forwardRef((props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  if (!hasContents(props)) {
    return null;
  }
  const { disabled } = props;
  const { contents, attributes } = createContent(props);
  return (
    <button type="button" {...attributes} disabled={disabled} ref={ref}>
      {contents}
    </button>
  );
});

const getTextLinkProps = ({
  className: extraClasses = '',
  inline,

  // a11y
  'aria-label': ariaLabel = '',

  // qa
  dataTestId = '',

  // rest
  ...rest
}: LinkProps) => ({
  className: classNames({
    'default-text-link': true,
    'inline-link': inline,
    [extraClasses]: true,
  }),
  title: ariaLabel || undefined,
  'aria-label': ariaLabel || undefined,
  'data-testid': dataTestId || undefined,
  ...rest,
});

export const createLinkComponent = <T extends React.ElementType, P = ComponentProps<T>>(
  Component: T,
) => {
  // KU – workaround for obscure "is not assignable to type 'LibraryManagedAttributes<T, any>'" error
  const TypedComponent: React.ElementType = Component;
  return (props: P & LinkProps & Omit<ComponentProps<'a'>, keyof P>) => {
    if (!hasContents(props)) {
      return null;
    }
    const { shape, disabled, href } = props;
    const { external, ...renderProps } = props;
    const LinkComponent = external ? 'a' : TypedComponent;

    const hrefProp = disabled ? '#' : href;

    // plain text link
    if (!shape) {
      return <LinkComponent {...getTextLinkProps(renderProps)} href={hrefProp} />;
    }

    // handle a disabled link
    const disabledAttributes = disabled && {
      'aria-disabled': true,
    };
    const { contents, attributes } = createContent(renderProps);
    return (
      <LinkComponent {...attributes} {...disabledAttributes} href={hrefProp}>
        {contents}
      </LinkComponent>
    );
  };
};
